import React from "react"
import PropTypes from "prop-types"
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts"
import { formatCurrency, formatNumber, formatPercent, formatGrowth, colorPara } from "../helpers"

const StatCard = ({ icon, label, value, accent = "#3b82f6", sub }) => (
  <div
    style={{
      flex: "1 1 200px",
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 14,
      padding: "18px 20px",
      minWidth: 0,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <div
        style={{
          width: 36, height: 36, borderRadius: 10,
          background: `${accent}1a`, color: accent,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 18, flexShrink: 0,
        }}
      >
        <i className={`mdi ${icon}`} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </span>
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{sub}</div>}
  </div>
)

StatCard.propTypes = { icon: PropTypes.string, label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node }

const TooltipContent = ({ active, payload }) => {
  if (!active || !payload || !payload[0]) return null
  const d = payload[0].payload
  return (
    <div style={{
      background: "#0f172a", color: "#fff",
      padding: "8px 12px", borderRadius: 8, fontSize: 12,
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)", maxWidth: 280,
    }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{d.sucursal}</div>
      <div>{formatCurrency(d.total)}</div>
      {typeof d.documentos === "number" && (
        <div style={{ color: "#94a3b8", fontSize: 11 }}>{formatNumber(d.documentos)} documentos</div>
      )}
    </div>
  )
}

TooltipContent.propTypes = { active: PropTypes.bool, payload: PropTypes.array }

const ResumenTab = ({ data }) => {
  if (!data) return null

  const resumen = data.resumen || {}
  const sucursales = data.sucursales || []
  const kpiGlobal = data.kpis?.global || {}
  const comparativo = data.comparativo || {}

  const totalVentas = sucursales.reduce((sum, s) => sum + (Number(s.total) || 0), 0)
  const totalDocumentos = sucursales.reduce((sum, s) => sum + (Number(s.total_documentos) || 0), 0)

  const ventasPorSucursal = [...sucursales]
    .map(s => ({
      sucursal: s.sucursal || "(Sin nombre)",
      sucursalCorta: (s.sucursal || "").length > 24 ? `${s.sucursal.slice(0, 23)}…` : s.sucursal || "(Sin nombre)",
      total: Number(s.total) || 0,
      documentos: Number(s.total_documentos) || 0,
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 10)

  const crecAnt = formatGrowth(comparativo.global_crecimiento_vs_anterior_pct)
  const crecYoY = formatGrowth(comparativo.global_crecimiento_yoy_pct)

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Cards globales */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <StatCard
          icon="mdi-cash-multiple"
          label="Ventas totales"
          value={formatCurrency(totalVentas)}
          accent="#10b981"
          sub={`${formatNumber(resumen.total_sucursales || sucursales.length)} sucursales`}
        />
        <StatCard
          icon="mdi-receipt-text"
          label="Documentos"
          value={formatNumber(totalDocumentos)}
          accent="#3b82f6"
        />
        <StatCard
          icon="mdi-ticket-percent-outline"
          label="Ticket promedio"
          value={formatCurrency(kpiGlobal.ticket_promedio)}
          accent="#8b5cf6"
          sub={kpiGlobal.ticket_mediano != null ? `Mediano: ${formatCurrency(kpiGlobal.ticket_mediano)}` : null}
        />
        <StatCard
          icon="mdi-receipt-text-remove-outline"
          label="% Notas crédito"
          value={formatPercent(kpiGlobal.pct_notas_credito ?? 0)}
          accent="#f59e0b"
        />
      </div>

      {/* Comparativos */}
      {(comparativo.global_crecimiento_vs_anterior_pct != null || comparativo.global_crecimiento_yoy_pct != null) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          {comparativo.global_crecimiento_vs_anterior_pct != null && (
            <div
              style={{
                flex: "1 1 280px",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                padding: "16px 20px",
                borderLeft: `4px solid ${crecAnt.color}`,
              }}
            >
              <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4, marginBottom: 6 }}>
                vs período anterior
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: crecAnt.color }}>
                  <i className={`mdi mdi-${crecAnt.icon} me-1`} />
                  {crecAnt.text}
                </span>
                <span style={{ fontSize: 13, color: "#64748b" }}>
                  {formatCurrency(comparativo.global_anterior)} → {formatCurrency(comparativo.global_actual)}
                </span>
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                {comparativo.periodo_anterior || "—"} vs {comparativo.periodo_actual || "—"}
              </div>
            </div>
          )}
          {comparativo.global_crecimiento_yoy_pct != null && (
            <div
              style={{
                flex: "1 1 280px",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                padding: "16px 20px",
                borderLeft: `4px solid ${crecYoY.color}`,
              }}
            >
              <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4, marginBottom: 6 }}>
                vs año anterior (YoY)
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: crecYoY.color }}>
                  <i className={`mdi mdi-${crecYoY.icon} me-1`} />
                  {crecYoY.text}
                </span>
                <span style={{ fontSize: 13, color: "#64748b" }}>
                  {formatCurrency(comparativo.global_yoy)} → {formatCurrency(comparativo.global_actual)}
                </span>
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                {comparativo.periodo_yoy || "—"} vs {comparativo.periodo_actual || "—"}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Top sucursales */}
      {ventasPorSucursal.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
            <i className="mdi mdi-chart-bar me-2 text-primary" />
            Top {ventasPorSucursal.length} sucursales por ventas
          </h6>
          <div style={{ width: "100%", height: Math.max(280, ventasPorSucursal.length * 36) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ventasPorSucursal} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={v => formatCurrency(v)} />
                <YAxis type="category" dataKey="sucursalCorta" width={170} tick={{ fontSize: 12, fill: "#475569" }} interval={0} />
                <Tooltip content={<TooltipContent />} cursor={{ fill: "rgba(59,130,246,0.08)" }} />
                <Bar dataKey="total" radius={[0, 6, 6, 0]}>
                  {ventasPorSucursal.map((d, i) => <Cell key={i} fill={colorPara(i)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  )
}

ResumenTab.propTypes = { data: PropTypes.object }

export default ResumenTab
